import { useEffect, useCallback, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Plus, X, TerminalSquare, ChevronDown, Bot, Check, PictureInPicture2, ListTree } from 'lucide-react';
import AllSessionsModal from './AllSessionsModal';
import { getPopoutContext } from '@/lib/popoutContext';
import TerminalAttachButton from '@/components/terminal/TerminalAttachButton';
import TerminalMicButton from '@/components/terminal/TerminalMicButton';
import InputHistoryButton from '@/components/terminal/InputHistoryButton';
import { useBackend, isCefHost, CefBackend } from '@/services/backend';
import { useTerminalStore } from '@/stores/terminalStore';
import { useTerminalLayoutStore } from '@/stores/terminalLayoutStore';
import { useThemeStore } from '@/stores/themeStore';
import { useProjectStore } from '@/stores/projectStore';
import { useSettingsStore } from '@/stores/settingsStore';
import { AGENT_CLI_CONFIG, buildAgentCommand } from '@/lib/agentCliConfig';
import { isGenericDaemonTitle, agentDisplayName, getNextTitle } from '@/lib/terminalTitles';
import { getProjectTerminalSettings, setProjectTerminalSettings, SHELL_CONFIGS } from '@/lib/projectSettings';
import { useAvailableShells } from '@/hooks/useAvailableShells';
import TerminalView from '@/components/terminal/TerminalView';
import RunButton from '@/components/workspace/RunButton';
import { logger } from '@/lib/logger';
import { uint8ToBase64 } from '@/lib/base64';
import { showToast } from '@/components/ui/Toast';
import { isWeb } from '@/lib/platform';

const TAG = 'TerminalTab';

/** Hard cap on active terminal sessions per project. Prevents a runaway
 *  re-render loop / spammed RPC client from spawning hundreds of PTYs.
 *  Bug observed in the wild (2026-05-02): WebRTC client connect kicked off
 *  a loop that opened ~250 sessions before something gave out. We cap here
 *  in the renderer AND on the daemon side as belt-and-suspenders. */
const MAX_SESSIONS_PER_PROJECT = 15;

/** Minimum delay between createTerminal calls for the same project. Catches
 *  the same runaway-loop class even when sessions get closed quickly. */
const CREATE_THROTTLE_MS = 500;

/** Module-level map so the throttle survives TerminalTab remounts. */
const lastCreateAt = new Map<string, number>();

/** Projects whose saved-layout restore is in flight. Guards against a core
 *  reconnect re-entering the sync effect mid-restore and double-spawning (the
 *  restore bypasses the throttle, so this is its re-entrancy guard instead). */
const restoringProjects = new Set<string>();

interface TerminalTabProps {
  projectPath?: string;
  agentType?: string;
}

// Use SHELL_CONFIGS from projectSettings for consistency
// Title helpers (isGenericDaemonTitle / agentDisplayName / getNextTitle) live in
// lib/terminalTitles so the startup session-adopter can share them.

export default function TerminalTab({ projectPath, agentType }: TerminalTabProps) {
  const backend = useBackend();
  const store = useTerminalStore();
  const availableShells = useAvailableShells();
  const [renamingId, setRenamingId] = useState<number | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const renameInputRef = useRef<HTMLInputElement>(null);

  // Dropdown state
  const [menuOpen, setMenuOpen] = useState(false);
  // Daemon-wide session list / orphan surface (086).
  const [allSessionsOpen, setAllSessionsOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const chevronRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const safePath = projectPath ?? '__default__';

  // Terminal chrome palette. The panel is theme-aware so it doesn't read as a
  // dark slab inside the light shell. Dark mode is byte-identical to before;
  // light mode mirrors the same elevation into light stone shades.
  const isLightTheme = useThemeStore((s) => s.theme) === 'atelier-light';
  const tc = isLightTheme
    ? {
        panel: 'bg-stone-100',
        bar: 'bg-stone-200 border-stone-300',
        tabActive: 'bg-stone-300 text-stone-900',
        tabIdle: 'text-stone-600 hover:text-stone-900 hover:bg-stone-300/60',
        divider: 'bg-stone-300',
        ctrlActive: 'bg-stone-300 text-stone-900',
        ctrl: 'text-stone-600 hover:text-stone-900 hover:bg-stone-300/60',
        renameInput: 'bg-white text-stone-900',
        muted: 'text-stone-500',
        menu: 'bg-stone-50 border-stone-300',
        menuItem: 'text-stone-700 hover:bg-stone-200 hover:text-stone-900',
        menuIcon: 'text-stone-500',
        menuDivider: 'border-stone-300',
      }
    : {
        panel: 'bg-stone-900',
        bar: 'bg-stone-800 border-stone-700',
        tabActive: 'bg-stone-700 text-stone-100',
        tabIdle: 'text-stone-400 hover:text-stone-200 hover:bg-stone-700/50',
        divider: 'bg-stone-600',
        ctrlActive: 'bg-stone-700 text-stone-200',
        ctrl: 'text-stone-500 hover:text-stone-300 hover:bg-stone-700/50',
        renameInput: 'bg-stone-600 text-stone-100',
        muted: 'text-stone-500',
        menu: 'bg-stone-800 border-stone-600',
        menuItem: 'text-stone-300 hover:bg-stone-700 hover:text-stone-100',
        menuIcon: 'text-stone-500',
        menuDivider: 'border-stone-700',
      };

  const projectSessions = store.getProjectSessions(safePath);
  const activeTerminalId = store.getActiveTerminalId(safePath);
  const activeSession = activeTerminalId != null
    ? projectSessions.find((s) => s.id === activeTerminalId) ?? null
    : null;

  const agentConfig = agentType ? AGENT_CLI_CONFIG[agentType] : null;

  // Click-outside handler for dropdown
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target as Node) &&
        chevronRef.current && !chevronRef.current.contains(e.target as Node)
      ) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const toggleMenu = useCallback(() => {
    setMenuOpen((prev) => {
      if (prev) return false;
      if (chevronRef.current) {
        const rect = chevronRef.current.getBoundingClientRect();
        setMenuPos({ top: rect.bottom + 4, left: Math.max(8, rect.right - 208) });
      }
      return true;
    });
  }, []);

  /**
   * Create a new terminal session.
   * - No args: uses project defaults (auto-launch agent with flags if configured).
   * - Explicit command + baseName: launches that command with that tab name.
   * - command=null + baseName: opens a plain shell with that tab name.
   */
  /**
   * Create a new terminal session.
   * - No args: uses project defaults (shell + auto-launch agent).
   * - Explicit command + baseName: launches that command with that tab name.
   * - command=null + baseName: opens a plain shell with that tab name.
   * - shellOverride: explicit shell type to use (bypasses project default).
   */
  const createTerminal = useCallback(
    async (
      command?: string | null,
      baseName?: string,
      agentSession?: boolean,
      shellOverride?: string,
      env?: Record<string, string>,
      meta?: { kind?: 'run' | 'auth'; runConfig?: string; restoreLayoutId?: string; bypassThrottle?: boolean },
    ): Promise<number | null> => {
      const existing = useTerminalStore.getState().getProjectSessions(safePath);

      // Trace caller — when the runaway-spawn bug fires again the stack
      // here pinpoints which effect/handler is the trigger.
      const callerStack = new Error().stack?.split('\n').slice(2, 6).join(' | ') ?? '?';
      logger.debug(TAG, `createTerminal entry safePath=${safePath} active=${existing.filter((s) => s.status === 'active').length} caller=${callerStack}`);

      // Belt-and-suspenders: cap active sessions per project to catch a
      // runaway loop before it spawns hundreds of PTYs. Counts only active
      // sessions (closed ones don't hold daemon resources).
      const activeForProject = existing.filter((s) => s.status === 'active');
      if (activeForProject.length >= MAX_SESSIONS_PER_PROJECT) {
        logger.warn(TAG, `Refusing createTerminal: project ${safePath} already at cap (${activeForProject.length}/${MAX_SESSIONS_PER_PROJECT})`);
        showToast(`Terminal cap reached (${MAX_SESSIONS_PER_PROJECT} per project) — close some tabs first.`, 'error');
        return null;
      }

      // Throttle so a render-loop spamming this can't outrun the cap by
      // having the user close tabs faster than they open. Restore (a controlled,
      // run-once batch that respawns a saved layout) bypasses it — otherwise the
      // 2nd..Nth tab of a 14-session restore would be refused — but the cap above
      // still applies as the hard safety net.
      const now = Date.now();
      const last = lastCreateAt.get(safePath) ?? 0;
      if (!meta?.bypassThrottle && now - last < CREATE_THROTTLE_MS) {
        logger.warn(TAG, `Refusing createTerminal: throttled (last=${now - last}ms ago, min=${CREATE_THROTTLE_MS}ms) for ${safePath}`);
        return null;
      }
      lastCreateAt.set(safePath, now);

      const currentProject = useProjectStore.getState().currentProject;
      const termSettings = getProjectTerminalSettings(currentProject?.settings ?? null);

      let finalCommand = command;
      let titleBase = baseName;
      let isAgent = agentSession ?? false;
      // True only for a brand-new project's very first auto-launch — we start a
      // FRESH session (no --continue) then flip the persisted marker below.
      let markFirstLaunch = false;

      // No explicit options → use project defaults
      if (finalCommand === undefined && titleBase === undefined) {
        if (termSettings.autoLaunchAgent && agentType && agentConfig) {
          // Resolve model: per-project preferredModel wins, then global default,
          // then nothing (agent CLI picks its own).
          const globalDefault = useSettingsStore.getState().agent.defaultModel[agentType];
          const model = termSettings.preferredModel || globalDefault || null;
          const isFirstLaunch = termSettings.agentHasLaunched === false;
          markFirstLaunch = isFirstLaunch;
          // Pass the shell so the workflow-append substitution matches (bash vs
          // PowerShell) — otherwise Windows/pwsh gets a broken --append-system-prompt.
          const launchShell = shellOverride ?? termSettings.defaultShell;
          finalCommand = buildAgentCommand(agentType, termSettings.agentFlags, termSettings.customAgentFlags, model, { isFirstLaunch, shell: launchShell });
          titleBase = agentConfig.displayName;
          isAgent = true;
        } else {
          finalCommand = null;
          titleBase = 'Terminal';
        }
      }

      // Determine which shell to use: explicit override > project default
      const shell = shellOverride ?? termSettings.defaultShell;
      const title = getNextTitle(titleBase ?? 'Terminal', existing);

      // Resolve the agent CLI key once (display name → key) — used for both
      // the daemon's completion pattern and the ATELIER_AGENT stamp below.
      const agentKey = isAgent
        ? Object.keys(AGENT_CLI_CONFIG).find((k) => AGENT_CLI_CONFIG[k].displayName === titleBase)
        : undefined;

      // Stamp session identity into the env. The daemon persists each
      // session's env, so these survive the UI — they're how an orphaned or
      // reload-adopted session is reconstituted (project linkage that's
      // immune to folder moves, what kind of tab it was, what was launched
      // in it). They're also introspectable by whatever runs inside the
      // terminal, like ATELIER_SHELL. Caller-supplied env wins on conflict
      // (the auth flow's CLAUDE_CONFIG_DIR must pin identity resolution).
      const kind = meta?.kind ?? (isAgent ? 'agent' : 'terminal');
      let identityName: string | undefined;
      try {
        const cfgRaw = currentProject && currentProject.root_path === projectPath
          ? await backend.files.readAteliercodeConfig(currentProject.id)
          : null;
        const override = cfgRaw ? (JSON.parse(cfgRaw) as { claudeIdentity?: string }).claudeIdentity : undefined;
        identityName = override ?? (await backend.identity.getDefault())?.name;
      } catch {
        // No identity registry / unreadable config — stamp nothing.
      }
      const stamp: Record<string, string> = {
        ...(currentProject && currentProject.root_path === projectPath
          ? { ATELIER_PROJECT_ID: currentProject.id }
          : {}),
        ATELIER_PROJECT_PATH: safePath,
        ATELIER_SESSION_KIND: kind,
        ...(agentKey ? { ATELIER_AGENT: agentKey } : {}),
        ...(finalCommand ? { ATELIER_LAUNCH_CMD: finalCommand } : {}),
        ...(meta?.runConfig ? { ATELIER_RUN_CONFIG: meta.runConfig } : {}),
        ...(identityName ? { ATELIER_CLAUDE_IDENTITY: identityName } : {}),
      };
      const mergedEnv = { ...stamp, ...(env ?? {}) };

      // Optimistic: add a placeholder tab immediately so the UI feels instant.
      // We use a negative temporary ID that gets swapped for the real one once
      // the PTY daemon responds.
      const tempId = -(Date.now() % 1_000_000);
      store.addSession(tempId, title, safePath, isAgent);

      try {
        // This is the slow part — PTY daemon may need to cold-start (5+ sec)
        const id = await backend.terminal.create(80, 24, projectPath, title, shell, mergedEnv);

        // Atomically swap the placeholder ID for the real one (no flash)
        store.replaceSessionId(tempId, id);

        // Durable layout record (so this tab can be rebuilt after the PTY is
        // truly gone — reboot / daemon death). A restore respawn just rebinds
        // its existing entry to the fresh PTY; a normal create adds a new one.
        // 'auth' sign-in flows are skipped inside the store.
        const layout = useTerminalLayoutStore.getState();
        if (meta?.restoreLayoutId) {
          layout.bindPty(safePath, meta.restoreLayoutId, id);
        } else {
          layout.recordCreate(safePath, {
            currentPtyId: id,
            title,
            isAgent,
            agentType: agentKey,
            kind,
            launchCmd: finalCommand,
            runConfig: meta?.runConfig,
            shell,
          });
        }

        // First launch done → persist the marker so subsequent launches
        // resume (--continue) as configured. Best-effort; never blocks the tab.
        if (markFirstLaunch) {
          const cp = useProjectStore.getState().currentProject;
          if (cp) {
            const settings = setProjectTerminalSettings(cp.settings ?? null, { agentHasLaunched: true });
            useProjectStore.getState().updateProject(cp.id, { settings })
              .catch((e) => logger.warn(TAG, 'failed to mark agentHasLaunched', e));
          }
        }

        // Tell Rust this is an agent session so it emits activity events when detached
        if (isAgent) {
          const pattern = agentKey ? AGENT_CLI_CONFIG[agentKey].completionPattern : undefined;
          backend.terminal.markAgentSession(id, true, pattern).catch((e) => logger.warn(TAG, 'markAgentSession failed', e));
        }

        // If there's a command to run, send it after shell starts
        if (finalCommand) {
          const sendCommand = (cmd: string) => {
            const encoder = new TextEncoder();
            const bytes = encoder.encode(cmd + '\r');
            const encoded = uint8ToBase64(bytes);
            backend.terminal.write(id, encoded).catch((e) => logger.warn(TAG, 'initial command write failed', e));
          };
          setTimeout(() => sendCommand(finalCommand), 300);
        }

        return id;
      } catch (e) {
        // Remove the placeholder on failure and surface the error to the user
        store.removeSession(tempId);
        const msg = e instanceof Error ? e.message : String(e);
        logger.warn(TAG, 'Failed to create terminal:', e);
        showToast(`Terminal failed to start: ${msg}`, 'error');
        return null;
      }
    },
    [backend, projectPath, safePath, store, agentType, agentConfig],
  );

  const handleMenuAgent = useCallback(
    (key: string) => {
      const config = AGENT_CLI_CONFIG[key];
      if (!config) return;
      // For the project's configured agent, use saved flags + preferredModel;
      // otherwise launch clean with whatever the user has set as the global
      // default model for that agent (still respects "no default" → undefined).
      const currentProject = useProjectStore.getState().currentProject;
      const termSettings = getProjectTerminalSettings(currentProject?.settings ?? null);
      let flags: string[] = [];
      let model: string | null = useSettingsStore.getState().agent.defaultModel[key] ?? null;
      if (key === agentType) {
        flags = termSettings.agentFlags;
        if (termSettings.preferredModel) model = termSettings.preferredModel;
      }
      // Match the workflow-append substitution to the shell it'll run in (the
      // project default here) so pwsh doesn't get bash syntax.
      createTerminal(buildAgentCommand(key, flags, undefined, model, { shell: termSettings.defaultShell }), config.displayName, true);
      setMenuOpen(false);
    },
    [agentType, createTerminal],
  );

  const handleMenuShell = useCallback(
    (shellId: string) => {
      const config = SHELL_CONFIGS[shellId as keyof typeof SHELL_CONFIGS];
      if (!config) return;
      // Pass shellId to spawn the correct shell natively
      createTerminal(null, config.name, false, shellId);
      setMenuOpen(false);
    },
    [createTerminal],
  );

  const closeTerminal = useCallback(
    async (id: number, e?: React.MouseEvent) => {
      e?.stopPropagation();
      try {
        await backend.terminal.close(id);
      } catch {
        // already closed
      }
      store.removeSession(id);
      // Manual close = forget this tab for good; it must never be recreated.
      // (Sessions that vanish because the daemon died are removed elsewhere via
      // store.removeSession WITHOUT this call, so they survive for recreate.)
      useTerminalLayoutStore.getState().removeByPty(safePath, id);
    },
    [backend, store, safePath],
  );

  // Re-sync whenever the core IPC (re)connects: the daemon is the source of
  // truth for live PTYs, and after a core restart or reload-reconnect this
  // mounted tab must re-adopt sessions it can't otherwise see. Bumping the
  // nonce re-runs the sync effect below; unmark first so its guard passes.
  const [syncNonce, setSyncNonce] = useState(0);
  useEffect(() => {
    const onReconnect = () => {
      useTerminalStore.getState().unmarkProjectInitialized(safePath);
      setSyncNonce((n) => n + 1);
    };
    window.addEventListener('ac:core-reconnected', onReconnect);
    return () => window.removeEventListener('ac:core-reconnected', onReconnect);
  }, [safePath]);

  // Sync sessions from daemon on every project switch, create if none exist
  useEffect(() => {
    // Mark as initialized SYNCHRONOUSLY at the start to prevent race conditions.
    // If the effect re-runs (due to dependency changes) before async work completes,
    // the second run will see this flag and exit immediately.
    if (useTerminalStore.getState().isProjectInitialized(safePath)) {
      return;
    }
    useTerminalStore.getState().markProjectInitialized(safePath);

    (async () => {
      try {
        // Fetch existing sessions from the daemon
        const remoteSessions = await backend.terminal.listSessions();
        // Match by the ATELIER_PROJECT_ID stamp when the session carries one —
        // exact linkage that survives folder moves and nested project roots.
        // Sessions from before stamping (no projectId) fall back to the cwd
        // heuristic. A stamped session NEVER matches by cwd: its id is
        // authoritative (prevents a parent-dir project adopting a nested
        // project's session).
        const thisProject = useProjectStore.getState().projects.find(
          (p) => p.root_path === safePath,
        );
        const forProject = remoteSessions.filter((s) => {
          if (s.status !== 'active') return false;
          if (s.projectId) return thisProject ? s.projectId === thisProject.id : false;
          return s.projectPath === safePath;
        });

        // Sync daemon sessions into store (add missing, remove stale)
        const storeState = useTerminalStore.getState();
        const localSessions = storeState.getProjectSessions(safePath);
        const remoteIds = new Set(forProject.map((s) => s.id));
        const localIds = new Set(localSessions.map((s) => s.id));

        // Add sessions that exist in daemon but not in store — and repair the
        // names of ones already present. The daemon returns the generic
        // "Terminal" title for every surviving PTY, but ALSO returns the agent
        // identity (agentType, from the persisted ATELIER_AGENT stamp). So for
        // an agent session whose daemon title is the bare default, re-derive the
        // tab name from the agent's displayName — this makes agent tabs
        // ("Claude Code", …) survive an app close/reopen instead of reverting
        // to "Terminal". A session the user hand-renamed to a non-generic string
        // isn't recoverable here (the daemon never got that string) and is left
        // to the one-shot localStorage snapshot.
        for (const s of forProject) {
          const agentName = agentDisplayName(s.agentType);
          if (!localIds.has(s.id)) {
            const title =
              agentName && isGenericDaemonTitle(s.title)
                ? getNextTitle(agentName, useTerminalStore.getState().getProjectSessions(safePath))
                : s.title;
            store.addSession(s.id, title, safePath, s.isAgent ?? false);
          } else if (agentName) {
            // Self-heal: a session already in the store (adopted before this
            // fix landed, or restored from the snapshot) that still shows the
            // generic daemon title but is really an agent session.
            const current = useTerminalStore.getState().sessions.get(s.id);
            if (current && isGenericDaemonTitle(current.title)) {
              store.renameSession(
                s.id,
                getNextTitle(agentName, useTerminalStore.getState().getProjectSessions(safePath)),
              );
            }
          }
          // Re-bind the Run button to an adopted still-running run-config
          // session (kind stamp), so "running" state + Stop survive a reload.
          if (s.kind === 'run' && useTerminalStore.getState().runSessionIds.get(safePath) == null) {
            useTerminalStore.getState().setRunSession(safePath, s.id);
          }
          // Converge the durable layout record with this live session — refresh
          // its entry, or adopt it if it predates the layout store. Keeps the
          // "what to rebuild after a reboot" record honest and complete.
          const storedNow = useTerminalStore.getState().sessions.get(s.id);
          useTerminalLayoutStore.getState().upsertLiving(safePath, {
            ptyId: s.id,
            title: storedNow?.title ?? s.title,
            isAgent: s.isAgent ?? false,
            agentType: s.agentType,
            kind: s.kind,
            launchCmd: s.launchCmd,
            runConfig: s.runConfig,
          });
        }

        // Remove sessions that exist locally but not in daemon (stale after a
        // restart).
        for (const s of localSessions) {
          if (!remoteIds.has(s.id)) {
            store.removeSession(s.id);
          }
        }

        // Keep the durable layout honest while the daemon is AUTHORITATIVE
        // (reported a non-empty live set): drop entries whose PTY is genuinely
        // gone, so crashed/finished tabs don't accumulate and spawn zombies on a
        // later reboot. Crucially gated on forProject.length > 0 — an EMPTY live
        // set is the reboot/daemon-death case, where the layout must be KEPT so
        // the tabs can be recreated below. (Manual closes already pruned their
        // own entries via removeByPty, so closing every tab empties the layout
        // and correctly yields no recreate.)
        if (forProject.length > 0) {
          for (const e of useTerminalLayoutStore.getState().getEntries(safePath)) {
            if (e.currentPtyId != null && !remoteIds.has(e.currentPtyId)) {
              useTerminalLayoutStore.getState().removeByPty(safePath, e.currentPtyId);
            }
          }
        }

        // If we have sessions now (from daemon or store), we're done
        const updatedSessions = useTerminalStore.getState().getProjectSessions(safePath);
        const activeSessions = updatedSessions.filter((s) => s.status === 'active');
        if (activeSessions.length > 0) {
          return;
        }
      } catch {
        // listSessions FAILED — most often the IPC WebSocket is mid-reconnect
        // (page reload, core restart). The daemon may well hold live sessions
        // for this project, so auto-spawning here creates duplicates and
        // orphans the real ones (the 2026-06-12 reload incident). Never create
        // on a failed sync: keep whatever the store has, un-mark init so the
        // next mount retries the sync once IPC is back.
        useTerminalStore.getState().unmarkProjectInitialized(safePath);
        return;
      }

      // No active sessions for this project. The desktop (Tauri) build is
      // the project owner — it should boot a default terminal so the
      // workspace isn't empty on first open. The web/PWA build is a
      // viewer attaching to a host machine's daemon, so spawning a fresh
      // PTY here on every page load means every time the user opens the
      // PWA on their phone they accumulate orphan shells on the host.
      // Skip the auto-spawn on web — the user can tap "+" to create one.
      if (isWeb()) {
        logger.debug(TAG, `Skipping auto-createTerminal for ${safePath} — web/PWA viewer mode`);
        return;
      }

      // Zero living sessions AND a saved layout = the PTYs are truly gone
      // (reboot / daemon death). Rebuild exactly the tabs the user had open —
      // same projects, same agents re-launched — instead of a lone default
      // terminal. This only runs when nothing is live, so it can never duplicate
      // a reattached session. Manually-closed tabs aren't here (removeByPty
      // dropped them), so they stay closed, as asked.
      const saved = useTerminalLayoutStore.getState().getEntries(safePath);
      if (saved.length > 0) {
        // A saved layout exists → never fall through to the single default
        // spawn. If another run is already restoring this project, defer to it.
        if (restoringProjects.has(safePath)) return;
        restoringProjects.add(safePath);
        try {
        logger.info(TAG, `Rebuilding ${saved.length} saved terminal(s) for ${safePath} (PTYs gone)`);
        let first: number | null = null;
        for (const entry of saved) {
          // Sequential + awaited so titles number correctly and the cap is
          // respected; bypassThrottle because this is a controlled batch.
          const common = { restoreLayoutId: entry.layoutId, bypassThrottle: true } as const;
          let id: number | null = null;
          if (entry.isAgent && entry.agentType && AGENT_CLI_CONFIG[entry.agentType]) {
            // Re-launch the agent fresh with CURRENT project/global settings
            // ("continuing what we have set up"). Pass the agent's displayName as
            // the base name so the ATELIER_AGENT stamp is derived correctly and
            // this tab is agent-marked again (so it survives the NEXT reboot too).
            const cfg = AGENT_CLI_CONFIG[entry.agentType];
            const cp = useProjectStore.getState().currentProject;
            const ts = getProjectTerminalSettings(cp?.settings ?? null);
            const isProjectAgent = entry.agentType === agentType;
            const flags = isProjectAgent ? ts.agentFlags : [];
            const customFlags = isProjectAgent ? ts.customAgentFlags : undefined;
            const model = (isProjectAgent && ts.preferredModel)
              ? ts.preferredModel
              : (useSettingsStore.getState().agent.defaultModel[entry.agentType] ?? null);
            const cmd = buildAgentCommand(entry.agentType, flags, customFlags, model, { shell: entry.shell ?? ts.defaultShell });
            id = await createTerminal(cmd, cfg.displayName, true, entry.shell, undefined, common);
          } else if (entry.kind === 'run') {
            id = await createTerminal(entry.launchCmd ?? null, entry.title, false, entry.shell, undefined, { ...common, kind: 'run', runConfig: entry.runConfig });
          } else {
            // Plain shell (or an agent we can no longer resolve) → named shell.
            id = await createTerminal(null, entry.title, false, entry.shell, undefined, common);
          }
          if (id != null) {
            if (first == null) first = id;
            if (entry.kind === 'run' && useTerminalStore.getState().runSessionIds.get(safePath) == null) {
              useTerminalStore.getState().setRunSession(safePath, id);
            }
          }
        }
        if (first != null) store.setActiveTerminal(safePath, first);
        return;
        } finally {
          restoringProjects.delete(safePath);
        }
      }

      // No saved layout → first-ever open for this project: one default terminal.
      createTerminal();
    })();
  }, [safePath, backend, store, createTerminal, syncNonce]);

  // Watch for pending run commands from RunButton (may be triggered from header)
  useEffect(() => {
    // Process any command that was set BEFORE this component mounted
    // (e.g. user clicked Run while on the Overview tab, then we switched here)
    const consumePending = () => {
      const pending = useTerminalStore.getState().pendingCommand;
      if (!pending || pending.projectPath !== safePath) return;
      useTerminalStore.getState().setPendingCommand(null);
      createTerminal(pending.command, pending.title, false, pending.shell, pending.env, {
        kind: pending.kind ?? 'run',
        runConfig: pending.runConfig,
      }).then((sessionId) => {
        if (sessionId !== null && (pending.kind ?? 'run') === 'run') {
          useTerminalStore.getState().setRunSession(safePath, sessionId);
        }
      });
    };

    // Check immediately on mount
    consumePending();

    // Subscribe to future changes
    const unsub = useTerminalStore.subscribe(() => consumePending());
    return unsub;
  }, [safePath, createTerminal]);

  // Clear run session when the terminal exits
  useEffect(() => {
    const unsub = useTerminalStore.subscribe((state) => {
      const runId = state.runSessionIds.get(safePath);
      if (runId == null) return;
      const session = state.sessions.get(runId);
      if (session && session.status === 'closed') {
        useTerminalStore.getState().setRunSession(safePath, null);
      }
    });
    return unsub;
  }, [safePath]);

  // Attach/detach lifecycle: when switching projects, detach previous project's
  // terminals (stops event emission, buffers to disk) and resume the new project's
  // terminals (resumes events). TerminalView handles reading the buffer on mount.
  useEffect(() => {
    const prevProject = useTerminalStore.getState().attachedProject;

    // Detach previous project's terminals
    if (prevProject && prevProject !== safePath) {
      const prevSessions = useTerminalStore.getState().getProjectSessions(prevProject);
      for (const s of prevSessions) {
        if (s.status === 'active') {
          backend.terminal.detach(s.id).catch((e) => logger.warn(TAG, 'detach failed', e));
        }
      }
    }

    // Resume current project's terminals (no buffer returned — TerminalView reads it)
    const currentSessions = useTerminalStore.getState().getProjectSessions(safePath);
    for (const s of currentSessions) {
      if (s.status === 'active') {
        backend.terminal.resume(s.id).catch((e) => logger.warn(TAG, 'resume failed', e));
      }
    }

    useTerminalStore.getState().setAttachedProject(safePath);

    return () => {
      // Detach on unmount
      const sessions = useTerminalStore.getState().getProjectSessions(safePath);
      for (const s of sessions) {
        if (s.status === 'active') {
          backend.terminal.detach(s.id).catch((e) => logger.warn(TAG, 'detach failed', e));
        }
      }
    };
  }, [safePath, backend]);

  // Right-click rename
  const handleContextMenu = useCallback(
    (e: React.MouseEvent, id: number) => {
      e.preventDefault();
      const session = store.sessions.get(id);
      if (session) {
        setRenamingId(id);
        setRenameValue(session.title);
      }
    },
    [store.sessions],
  );

  const commitRename = useCallback(() => {
    if (renamingId !== null && renameValue.trim()) {
      store.renameSession(renamingId, renameValue.trim());
    }
    setRenamingId(null);
  }, [renamingId, renameValue, store]);

  useEffect(() => {
    if (renamingId !== null) {
      renameInputRef.current?.focus();
      renameInputRef.current?.select();
    }
  }, [renamingId]);

  return (
    <div className={`flex flex-col h-full ${tc.panel}`}>
      {/* Inner tab bar */}
      <div className={`flex items-center h-9 border-b px-1 gap-0.5 shrink-0 ${tc.bar}`}>
        {projectSessions.map((s) => (
          <button
            key={s.id}
            onClick={() => store.setActiveTerminal(safePath, s.id)}
            onContextMenu={(e) => handleContextMenu(e, s.id)}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded text-xs transition-colors ${
              activeTerminalId === s.id ? tc.tabActive : tc.tabIdle
            }`}
          >
            <TerminalSquare className="w-3 h-3 shrink-0" />
            {renamingId === s.id ? (
              <input
                ref={renameInputRef}
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onBlur={commitRename}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitRename();
                  if (e.key === 'Escape') setRenamingId(null);
                }}
                className={`text-xs px-1 rounded w-20 outline-none ${tc.renameInput}`}
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <span className="truncate max-w-[100px]">{s.title}</span>
            )}
            {/* Per-tab "working" spinner removed — was unreliable (any
                screen redraw flagged activity, so it never stopped). The
                sidebar dot is the canonical activity indicator now. */}
            {s.visualState === 'completed' && (
              <Check className="w-3 h-3 text-green-400 shrink-0" />
            )}
            {s.visualState === 'exited' && (
              <span className={`text-[10px] ${tc.muted}`}>(exited)</span>
            )}
            <X
              className="w-3 h-3 opacity-50 hover:opacity-100 shrink-0"
              onClick={(e) => closeTerminal(s.id, e)}
            />
          </button>
        ))}

        {/* Split "New Terminal" button */}
        <div className="flex items-center">
          <button
            onClick={() => createTerminal()}
            className={`flex items-center justify-center h-6 px-1.5 rounded-l transition-colors ${tc.tabIdle}`}
            title={agentConfig ? `New ${agentConfig.displayName}` : 'New Terminal'}
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
          <div className={`w-px h-4 ${tc.divider}`} />
          <button
            ref={chevronRef}
            onClick={toggleMenu}
            className={`flex items-center justify-center h-6 px-1 rounded-r transition-colors ${
              menuOpen ? tc.ctrlActive : tc.ctrl
            }`}
            title="New terminal type"
          >
            <ChevronDown className="w-3 h-3" />
          </button>
        </div>

        {/* Run button */}
        <RunButton projectPath={projectPath} />

        {/* Spacer pushes buttons to right */}
        <div className="flex-1" />
        <PopoutButton />
        <TerminalAttachButton activeSessionId={activeTerminalId} projectPath={safePath} />
        <InputHistoryButton activeSessionId={activeTerminalId} projectPath={safePath} />
        <TerminalMicButton activeSessionId={activeTerminalId} projectPath={safePath} />
        <TerminalMicButton activeSessionId={activeTerminalId} projectPath={safePath} enhanced />
      </div>

      {/* Dropdown menu (portal to avoid parent clipping) */}
      {menuOpen &&
        createPortal(
          <div
            ref={menuRef}
            className={`fixed z-[9999] border rounded-lg shadow-xl py-1 w-52 ${tc.menu}`}
            style={{ top: menuPos.top, left: menuPos.left }}
          >
            {/* Agents section */}
            <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-stone-500 font-semibold select-none">
              Agents
            </div>
            {Object.entries(AGENT_CLI_CONFIG).map(([key, config]) => (
              <button
                key={key}
                onClick={() => handleMenuAgent(key)}
                className={`flex items-center gap-2 w-full px-3 py-1.5 text-xs cursor-pointer transition-colors text-left ${tc.menuItem}`}
              >
                <Bot className="w-3.5 h-3.5 text-stone-500 shrink-0" />
                <span>{config.displayName}</span>
                {key === agentType && (
                  <span className="ml-auto text-[10px] text-amber-500/70">default</span>
                )}
              </button>
            ))}

            <div className={`border-t my-1 ${tc.menuDivider}`} />

            {/* Shells section */}
            <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-stone-500 font-semibold select-none">
              Shells
            </div>
            {availableShells.map((shell) => (
              <button
                key={shell.id}
                onClick={() => handleMenuShell(shell.id)}
                className={`flex items-center gap-2 w-full px-3 py-1.5 text-xs cursor-pointer transition-colors text-left ${tc.menuItem}`}
              >
                <TerminalSquare className="w-3.5 h-3.5 text-stone-500 shrink-0" />
                <span>{shell.name}</span>
              </button>
            ))}

            <div className={`border-t my-1 ${tc.menuDivider}`} />

            {/* Daemon-wide session list — sees orphans no project tab can. */}
            <button
              onClick={() => { setAllSessionsOpen(true); setMenuOpen(false); }}
              className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-stone-300 hover:bg-stone-700 hover:text-stone-100 cursor-pointer transition-colors text-left"
            >
              <ListTree className="w-3.5 h-3.5 text-stone-500 shrink-0" />
              <span>All sessions…</span>
            </button>
          </div>,
          document.body,
        )}

      {allSessionsOpen && (
        <AllSessionsModal currentProjectPath={safePath} onClose={() => setAllSessionsOpen(false)} />
      )}

      {/* Terminal content area — only the ACTIVE tab gets an xterm.js instance */}
      <div className="flex-1 min-h-0 relative">
        {activeSession ? (
          <div key={activeSession.id} className="absolute inset-0">
            <TerminalView sessionId={activeSession.id} isActive={true} isAgent={activeSession.isAgent} agentType={agentType} />
          </div>
        ) : projectSessions.length === 0 ? (
          <div className={`flex items-center justify-center h-full text-sm ${tc.muted}`}>
            No terminals open
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Pop-out button for the TerminalTab header. Spawns a secondary OS window
 * hosting the terminal view for the current project; that popout window
 * has its own active-terminal selection (each Tauri window has its own
 * zustand instance) while the underlying PTY sessions stay shared on the
 * daemon — so typing in main and seeing it in popout both work.
 *
 * Hidden in popout windows themselves (no nested popouts) and on web.
 */
function PopoutButton() {
  const project = useProjectStore((s) => s.currentProject);
  const backend = useBackend();
  const isLight = useThemeStore((s) => s.theme) === 'atelier-light';
  const popoutCtx = getPopoutContext();
  const cefMode = isCefHost();
  const canPopout = cefMode && !popoutCtx.isPopout && !!project?.id;
  const handleClick = useCallback(async () => {
    if (!canPopout || !project) return;
    try {
      if (cefMode && backend instanceof CefBackend) {
        const label = `popout-terminal-${project.id}`;
        await backend.popout.open(label, 'terminal', project.id);
      }
    } catch (e) {
      logger.warn(TAG, 'popout_open failed', e);
    }
  }, [canPopout, cefMode, backend, project]);
  if (!canPopout) return null;
  return (
    <button
      type="button"
      onClick={handleClick}
      className={`flex items-center justify-center h-6 px-1.5 rounded transition-colors ${
        isLight
          ? 'text-stone-600 hover:text-stone-900 hover:bg-stone-300/60'
          : 'text-stone-400 hover:text-stone-200 hover:bg-stone-700/50'
      }`}
      title="Pop out into its own window"
    >
      <PictureInPicture2 className="w-3.5 h-3.5" />
    </button>
  );
}
